/**
 * PrototypesFunnelWidget - воронка времени в игре прототипов по проектам
 * =======================================================================
 *
 * Период из сайдбара — ДАТА УСТАНОВКИ когорты. Проекты выбираются здесь же (пусто = все).
 * Режим: «Весь лайфтайм» — суммарное время за все дни, D0 — только день установки.
 * График: линия на проект, X — шаг воронки, Y — % от регистрации.
 * Таблица: строки — шаги, колонки — проекты (игроки и % от регистрации; % от прошлого шага в title).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { BaseWidget, useBaseWidget } from './BaseWidget';
import { useWidgetData } from '../../hooks/useWidgetData';
import { ApiService } from '../../services/api';
import { FiltersService } from '../../services/filtersService';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import { ProjectFilter } from './PrototypesRetentionWidget';
import { filterByMinRegistrations, HiddenProjectsNote } from './prototypesMinRegistrations';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import { Tooltip } from '../charts/OrderedTooltip';
import { ResponsiveChart as ResponsiveContainer } from '../ui/responsive-chart';
import { prototypes as prototypesSpecs } from '@ggstat/chart-specs';

const PROJECT_COLORS = prototypesSpecs.funnel.projectPalette;
const MODES = prototypesSpecs.funnel.modes;
const PLAYTIME_SOURCES = prototypesSpecs.funnel.playtimeSources;
const PLAYTIME_NOTES = prototypesSpecs.funnel.playtimeNotes;

const fmtInt = (value) => Number(value || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 });
const fmtDuration = (sec) => {
  if (sec === null || sec === undefined) return '—';
  const s = Math.round(Number(sec));
  if (s < 60) return `${s} сек`;
  const m = Math.floor(s / 60);
  const rest = s % 60;
  if (m < 60) return rest ? `${m} мин ${rest} сек` : `${m} мин`;
  return `${Math.floor(m / 60)} ч ${m % 60} мин`;
};
const PLAYTIME_STAT_ROWS = prototypesSpecs.funnel.playtimeStatRows;
const fmtPct = (value) => (value === null || value === undefined ? '—' : `${value}%`);

// dataKey серий — p0, p1, …: имя проекта может содержать символы, ломающие путь recharts
const CustomTooltip = ({ active, payload, label }) => {
  const visible = (payload || []).filter((entry) => !entry.hide);
  if (!active || visible.length === 0) return null;
  return (
    <div className="bg-background border rounded-lg shadow-lg p-3">
      <p className="font-medium mb-2">{label}</p>
      {visible.map((entry) => (
        <p key={entry.dataKey} className="text-sm" style={{ color: entry.color }}>
          {entry.name}: {fmtPct(entry.value)} ({fmtInt(entry.payload[`${entry.dataKey}__users`])})
        </p>
      ))}
    </div>
  );
};

export function PrototypesFunnelWidget({ minRegistrations = 0 }) {
  const [projectOptions, setProjectOptions] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [mode, setMode] = useState('lifetime');
  const [playtime, setPlaytime] = useState('gap1m');
  const [hiddenLines, setHiddenLines] = useState({});
  // entry.value — имя проекта: ключ не зависит от индекса серии, который сдвигается при смене состава
  const toggleLine = (entry) => setHiddenLines((prev) => ({ ...prev, [entry.value]: !prev[entry.value] }));
  const projectsKey = selectedProjects.join(',');

  useEffect(() => {
    let cancelled = false;
    ApiService.get('/prototypes/projects')
      .then((list) => {
        if (!cancelled) setProjectOptions(Array.isArray(list) ? list : []);
      })
      .catch((error) => console.error('Не удалось загрузить проекты прототипов:', error?.message || error));
    return () => {
      cancelled = true;
    };
  }, []);

  const widgetData = useWidgetData(
    async (filters, options) => {
      const params = {
        from: filters.startDate ? FiltersService.formatDate(filters.startDate) : undefined,
        to: filters.endDate ? FiltersService.formatDate(filters.endDate) : undefined,
        project: selectedProjects,
        mode,
        playtime
      };
      return ApiService.get('/prototypes/funnel', params, { signal: options?.signal });
    },
    [projectsKey, mode, playtime]
  );

  const widgetProps = useBaseWidget(widgetData);
  const allProjects = useMemo(
    () => (Array.isArray(widgetProps.data?.projects) ? widgetProps.data.projects : []),
    [widgetProps.data]
  );
  const projects = useMemo(
    () => filterByMinRegistrations(allProjects, minRegistrations, (project) => project.registrations),
    [allProjects, minRegistrations]
  );

  // Шаги одинаковы у всех проектов — берём подписи из первого
  const stepLabels = useMemo(() => (projects[0]?.steps || []).map((step) => step.label), [projects]);

  // Широкий формат для recharts: { label, [project]: pct, [project__users]: users }
  const chartData = useMemo(
    () =>
      stepLabels.map((label, index) => {
        const row = { label };
        projects.forEach((project, projectIndex) => {
          const step = project.steps[index];
          row[`p${projectIndex}`] = step?.pctOfRegistration ?? null;
          row[`p${projectIndex}__users`] = step?.users ?? 0;
        });
        return row;
      }),
    [projects, stepLabels]
  );

  const toggleProject = (project) => {
    setSelectedProjects((prev) =>
      prev.includes(project) ? prev.filter((name) => name !== project) : [...prev, project].sort()
    );
  };

  return (
    <BaseWidget
      {...widgetProps}
      title="Прототипы: воронка времени в игре"
      subtitle="Когорта по первому first_open. Период — даты установки"
      emptyMessage="Нет данных за выбранный период"
      skeletonRows={8}
      headerAction={
        <ProjectFilter
          options={projectOptions}
          selected={selectedProjects}
          onToggle={toggleProject}
          onClear={() => setSelectedProjects([])}
        />
      }
    >
      <div className="space-y-6">
        <div className="flex flex-wrap gap-4">
          <ToggleGroup
            type="single"
            value={mode}
            onValueChange={(value) => value && setMode(value)}
            size="sm"
          >
            {MODES.map((item) => (
              <ToggleGroupItem key={item.value} value={item.value} aria-label={item.label}>
                {item.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <ToggleGroup
            type="single"
            value={playtime}
            onValueChange={(value) => value && setPlaytime(value)}
            size="sm"
          >
            {PLAYTIME_SOURCES.map((item) => (
              <ToggleGroupItem key={item.value} value={item.value} aria-label={item.label}>
                {item.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      <HiddenProjectsNote hidden={allProjects.length - projects.length} minRegistrations={minRegistrations} />
      {projects.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {allProjects.length === 0 ? 'Нет когорт установки в выбранном периоде' : 'Нет проектов с таким числом регистраций'}
        </p>
      ) : (
        <div className="space-y-6">
          <div className="space-y-2">
            <ResponsiveContainer width="100%" height={380}>
              <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="label" tick={{ fill: 'currentColor' }} className="text-xs" interval={0} angle={-35} textAnchor="end" height={70} />
                <YAxis
                  domain={[0, 100]}
                  tick={{ fill: 'currentColor' }}
                  className="text-xs"
                  label={{ value: '% от регистрации', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend onClick={toggleLine} wrapperStyle={{ cursor: 'pointer' }} />
                {projects.map((project, index) => (
                  <Line
                    key={project.project}
                    type="monotone"
                    dataKey={`p${index}`}
                    name={project.project}
                    hide={!!hiddenLines[project.project]}
                    stroke={PROJECT_COLORS[index % PROJECT_COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 2 }}
                    activeDot={{ r: 4 }}
                    isAnimationActive={false}
                    connectNulls={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
            <p className="text-xs text-muted-foreground">
              {PLAYTIME_NOTES[playtime]}
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">Шаг</th>
                  {projects.map((project) => (
                    <th key={project.project} className="py-2 pr-4 font-medium text-right">{project.project}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stepLabels.map((label, index) => (
                  <tr key={label} className="border-b last:border-0">
                    <td className="py-2 pr-4 font-medium">{label}</td>
                    {projects.map((project) => {
                      const step = project.steps[index];
                      return (
                        <td
                          key={project.project}
                          className="py-2 pr-4 text-right"
                          title={index === 0 ? undefined : `${fmtPct(step?.pctOfPrevious)} от предыдущего шага`}
                        >
                          {fmtPct(step?.pctOfRegistration)} ({fmtInt(step?.users)})
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {PLAYTIME_STAT_ROWS.map((row, rowIndex) => (
                  <tr key={row.key} className={rowIndex === 0 ? 'border-t-2' : 'border-b last:border-0'}>
                    <td className="py-2 pr-4 font-medium">{row.label}</td>
                    {projects.map((project) => {
                      const stats = project.playtimeStats;
                      return (
                        <td
                          key={project.project}
                          className="py-2 pr-4 text-right"
                          title={row.played && stats ? `игравших (время > 0): ${fmtInt(stats.played)}` : undefined}
                        >
                          {stats ? fmtDuration(stats[row.key]) : '—'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {playtime === 'session_end' && (
                <tr className="border-t-2">
                  <td className="py-2 pr-4 font-medium">Доля сессий без session_end</td>
                  {projects.map((project) => {
                    const s = project.sessionsWithoutEnd;
                    return (
                      <td key={project.project} className="py-2 pr-4 text-right">
                        {s && s.total > 0
                          ? `${fmtPct(s.pct)} (${fmtInt(s.missing)} из ${fmtInt(s.total)})`
                          : '—'}
                      </td>
                    );
                  })}
                </tr>
                )}
              </tbody>
            </table>
            <p className="mt-2 text-xs text-muted-foreground">
              % от регистрации (игроки); % от предыдущего шага — во всплывающей подсказке ячейки.
              Медиана и среднее — время на игрока в выбранном режиме (D0 / за всё время) и способе подсчёта; «все» включают игроков с нулевым временем.
              {playtime === 'session_end' && ' Сессия — пара (device_id, session_id) с session_start у устройств когорты.'}
            </p>
          </div>
        </div>
      )}
      </div>
    </BaseWidget>
  );
}

export default PrototypesFunnelWidget;
